import { evaluateApplication } from './applicationScoring';

// Application result types
export const ApplicationResult = {
  CALENDLY: 'CALENDLY',
  WAITLIST: 'WAITLIST',
  REVIEW: 'REVIEW'
};

const analyzeResponse = async (responses) => {
  try {
    const prompt = `
      Analyze this beta application for Moogul, a Strategy Studio for small businesses.
      The ideal beta tester should:
      - Have a clear growth trajectory and strategic goals
      - Be actively managing business strategy (even if inefficiently)
      - Show genuine pain points around strategy/content management
      - Have capacity and willingness to provide meaningful feedback
      - Be in our target market (solopreneurs, small teams, or agencies)

      Application responses:
      Business Type: ${responses.businessType}
      Current Tools: ${responses.currentTools.join(', ')}
      Growth Focus: ${responses.growthFocus}
      AI Experience: ${responses.aiExperience}
      Biggest Challenge: ${responses.biggestChallenge}
      Time Commitment: ${responses.timeCommitment}
      Success Story Permission: ${responses.successStoryPermission}

      Analyze the application holistically and return a JSON response with:
      1. Overall recommendation (must be exactly one of: ${Object.values(ApplicationResult).join(', ')})
      2. Confidence score (0-100)
      3. Key factors that led to this decision
      4. Risk factors or concerns
      5. Suggested focus areas for onboarding if accepted
    `;

    const response = await fetch('/src/pages/api/analyze', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ prompt }),
    });

    if (!response.ok) {
      throw new Error('Failed to analyze application');
    }

    const analysis = await response.json();

    // Validate the recommendation is one of our expected values
    if (!Object.values(ApplicationResult).includes(analysis.recommendation)) {
      throw new Error('Invalid recommendation value from AI analysis');
    }

    // Map the AI's analysis to our application result format
    return {
      result: analysis.recommendation,
      confidence: analysis.confidence,
      score: {
        immediate: analysis.confidence >= 80 ? 10 : analysis.confidence >= 60 ? 7 : 4,
        waitlist: analysis.confidence < 40 ? 5 : 2,
        review: analysis.confidence >= 60 && analysis.confidence < 80 ? 3 : 0
      },
      message: generateMessage(analysis),
      metadata: {
        keyFactors: analysis.keyFactors,
        riskFactors: analysis.riskFactors,
        onboardingFocus: analysis.onboardingFocus
      }
    };
  } catch (error) {
    console.error('AI Analysis error:', error);
    // Fallback to traditional scoring if AI analysis fails
    const result = evaluateApplication(responses);
    return {
      ...result,
      result: result.result === 'CALENDLY' ? ApplicationResult.CALENDLY :
              result.result === 'WAITLIST' ? ApplicationResult.WAITLIST :
              ApplicationResult.REVIEW
    };
  }
};

const generateMessage = (analysis) => {
  switch (analysis.recommendation) {
    case ApplicationResult.CALENDLY:
      return `
        Thank you for applying to join Moogul's beta program! Based on our analysis, 
        we'd love to get you started right away.
        
        During your onboarding call, we'll focus on:
        ${analysis.onboardingFocus.map(focus => `- ${focus}`).join('\n')}
        
        Can't wait to help you transform your strategy into action!
      `;
    
    case ApplicationResult.WAITLIST:
      return `
        Thanks for your interest in Moogul! While we're focusing our beta on specific 
        use cases${analysis.riskFactors.length > 0 ? ` (${analysis.riskFactors[0].toLowerCase()})` : ''}, 
        we'd love to keep you updated on our progress.
        
        You'll get:
        - Early access when we expand
        - Founding member pricing
        - Strategy insights newsletter
        - Product development updates
      `;
    
    default:
      return `
        Thanks for your detailed application! We'd like to learn more about your needs 
        to ensure Moogul is the right fit.
        
        Specifically, we'd like to discuss:
        ${analysis.keyFactors.map(factor => `- ${factor}`).join('\n')}
        
        Expected response: Within 24 hours
        
        In the meantime:
        - Join our waitlist for updates
        - Check out our strategy resources
        - Save your founding member discount
      `;
  }
};

export { analyzeResponse }; 